<!-- eslint-disable vue/multi-word-component-names -->
<script setup>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { getAllArticles } from "../utils/articles.js";
import Header from "../components/Header.vue";
import Footer from "../components/Footer.vue";

const router = useRouter();
const articles = ref([]);

const articleCards = computed(() =>
  articles.value.map((a) => ({
    id: String(a.id),
    image: a.image,
    meta: [a.category, a.date].filter(Boolean).join(" · "),
    title: a.title,
    excerpt: a.excerpt || "",
    slug: a.slug,
  })),
);

const goToArticle = (slug) => {
  if (slug) router.push(`/article/${slug}`);
};

onMounted(async () => {
  try {
    const articlesObj = await getAllArticles();
    articles.value = Object.values(articlesObj).map((article) => ({
      id: article.id,
      image: article.image,
      title: article.title,
      date: article.date,
      category: article.category,
      excerpt: article.excerpt,
      slug: article.id,
    }));
  } catch {
    /* ignore */
  }
});
</script>

<template>
  <div
    class="flex min-h-screen flex-col bg-background font-body text-on-background selection:bg-secondary selection:text-on-secondary"
  >
    <Header />

    <main class="flex-1 pb-16 pt-20 sm:pt-24 md:pt-28">
      <section class="page-x mx-auto max-w-[1440px]">
        <div class="mb-8 sm:mb-12">
          <router-link
            to="/"
            class="font-label mb-4 inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-secondary transition-colors hover:text-primary sm:mb-6 sm:text-label-md"
          >
            <span class="material-symbols-outlined text-lg">arrow_back</span>
            Back to home
          </router-link>
          <p
            class="font-label mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-primary sm:mb-6 sm:text-label-md sm:tracking-[0.3em]"
          >
            Intellectual Property
          </p>
          <h1
            class="font-display text-[1.75rem] leading-tight text-primary sm:text-4xl md:text-7xl"
          >
            All Articles
          </h1>
        </div>

        <div
          v-if="articleCards.length"
          class="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-10 lg:grid-cols-3 lg:gap-8 xl:grid-cols-4"
        >
          <article
            v-for="item in articleCards"
            :key="item.id"
            class="group cursor-pointer"
            @click="goToArticle(item.slug)"
          >
            <div
              class="mb-5 aspect-[16/9] overflow-hidden rounded-xl shadow-lg sm:mb-8"
            >
              <img
                alt=""
                class="h-full w-full object-cover transition-transform duration-1000 ease-expo group-hover:scale-105"
                :src="item.image"
              />
            </div>
            <span
              class="font-label mb-3 block text-[10px] font-bold uppercase tracking-widest text-secondary sm:mb-4 sm:text-label-md"
              >{{ item.meta }}</span
            >
            <h2
              class="font-headline mb-3 text-lg leading-tight text-primary transition-colors group-hover:text-secondary sm:mb-4 sm:text-xl md:text-headline-lg"
            >
              {{ item.title }}
            </h2>
            <p
              v-if="item.excerpt"
              class="font-body line-clamp-3 leading-relaxed text-on-surface-variant"
            >
              {{ item.excerpt }}
            </p>
          </article>
        </div>
        <p v-else class="font-body text-on-surface-variant">
          New articles will appear here when published.
        </p>
      </section>
    </main>

    <Footer />
  </div>
</template>
